import SessionStorageService from '@services/SessionStorageService';
import WorkspaceContextService from '@services/WorkspaceContextService';
import LocalStorageKey from '@enums/LocalStorageKey';
import { MasqueradeUser } from '@customTypes/auth/Auth';

/**
 * Assemble the request headers that carry cross-cutting per-tab auth
 * context: masquerade impersonation and the X-Workspace-Id pin.
 *
 * Both live in sessionStorage so tabs stay independent — starting a
 * masquerade or switching workspaces in one tab does not follow the
 * user's other tabs.
 *
 * The axios interceptor in ApiService applies these to every REST call;
 * raw fetch() SSE sites in NewConversationService (which bypass axios)
 * call this to stay in lockstep. Consolidating here means a future
 * per-request header only needs to be added in one place.
 */
export function attachAuthHeaders(
  headers: Record<string, string>,
  opts: { skipMasquerade?: boolean } = {},
): Record<string, string> {
  // The masquerade picker (/users/verified) is a superuser-only action that
  // must resolve on the OPERATOR's identity even mid-masquerade — the backend
  // swaps g.user_profile to the (non-superuser) customer when this header is
  // present, so a masquerading operator would 403. Callers pass skipMasquerade
  // to keep such requests operator-scoped.
  const masquerade = SessionStorageService.getItem<MasqueradeUser>(
    LocalStorageKey.MASQUERADE_USER,
  );
  if (!opts.skipMasquerade && masquerade?.id) {
    headers.Masquerade = masquerade.id;
  }

  const workspacePin = WorkspaceContextService.headerValue();
  if (workspacePin) {
    headers['X-Workspace-Id'] = workspacePin;
  }

  return headers;
}
